import React from 'react';
import { Box, Button, IconButton } from '@mui/material';
import { VolumeUp as SpeakerIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import UnitPageShell from '../../components/shared/UnitPageShell';
import ZoomableImage from '../../components/shared/ZoomableImage';
import EditableTextBlock from '../../components/shared/EditableTextBlock';
import { useAuth } from '../../context/AuthContext';
import { useTTS } from '../../hooks/useTTS';
import { uploadUnitAsset, updateUnitContent, useUnitOverride } from '../../services/unitOverridesService';

const PAGE_ID = 'm6_u4_x3_stetige';
const IMAGE_BASE = '/assets/drawable/';

type TextBlock = { heading: string; body: string };

const textBlocks: TextBlock[] = [
  { heading: 'Einführung', body: 'Stetige Klappenantriebe mit 0–10V oder über Bussysteme erlauben präzise, stufenlose Positionsregelung und sind Standard in modernen Lüftungsanlagen.' },
  { heading: 'Ansteuerung', body: '• 0–10V: analoger Sollwert für Klappenposition\n• Bus (z. B. Modbus/ BACnet): digitale Kommunikation und Diagnose' },
  { heading: 'Elektrischer Anschluss', body: '• Versorgung (z. B. 24V)\n• Analogeingang 0–10V bzw. Bus-Schnittstelle\n• Optional: Rückmeldeausgänge' },
  { heading: 'Einsatzbereiche', body: '• Präzise Luftmengenregelung\n• GA/GLT-Integration mit Rückmeldung\n• Energetisch optimierte Anlagen' },
  { heading: 'Fazit', body: 'Stetige Antriebe bieten höchste Regelqualität. Voraussetzung sind korrekte Signalführung und Parametrierung in der DDC/GLT.' }
];

const images = [
  { id: 'kas_beispiel', label: 'Stetiger Klappenantrieb', src: `${IMAGE_BASE}klappe_stetig.jpg`, alt: 'Beispiel eines stetigen Klappenantriebs' }
];

const Page: React.FC = () => {
  const navigate = useNavigate();
  const { token, canEditTextsM6, canUploadImagesM6 } = useAuth();
  const canEditTexts = canEditTextsM6;
  const canUploadImages = canUploadImagesM6;

  const { override, refresh } = useUnitOverride(PAGE_ID);

  const mergedBlocks = React.useMemo(
    () => textBlocks.map((tb, i) => ({ ...tb, body: override?.texts?.[`block_${i}`] || tb.body })),
    [override]
  );

  const imgSrc = (key: string, fallback: string) => {
    const v = override?.images?.[key];
    if (!v) return fallback;
    const s = String(v);
    return s.startsWith('/assets/') || s.startsWith('assets/') ? s : `/assets/unit_custom/${s}`;
  };

  const fullPlainText = React.useMemo(() => mergedBlocks.map(b => `${b.heading}. ${b.body}`).join(' '), [mergedBlocks]);
  const { speak, stop, speaking } = useTTS({ text: fullPlainText });

  const handleImageReplace = React.useCallback(async (imageKey: string, file: File) => {
    if (!token) return alert('Kein Token – bitte einloggen.');
    try {
      await uploadUnitAsset(PAGE_ID, imageKey, file, token);
      alert('Bild erfolgreich aktualisiert.');
      await refresh();
    } catch (e: any) {
      alert('Upload fehlgeschlagen: ' + (e?.message || e));
    }
  }, [token, refresh]);

  const handleTextSave = React.useCallback(async (blockId: string, newBody: string) => {
    if (!token) return alert('Kein Token – bitte einloggen.');
    try {
      await updateUnitContent(PAGE_ID, { texts: { [blockId]: newBody } }, token);
      alert('Text gespeichert.');
      await refresh();
    } catch (e: any) {
      alert('Speichern fehlgeschlagen: ' + (e?.message || e));
    }
  }, [token, refresh]);

  const ttsButton = React.createElement(IconButton, { onClick: speaking ? stop : speak, 'aria-label': 'Text vorlesen', sx: { ml: 1 } }, React.createElement(SpeakerIcon, { sx: { fontSize: 28 } }));

  return React.createElement(UnitPageShell, { title: '6.4.3 Klappenantriebe – Stetig', actions: ttsButton },
    React.createElement(Box, { sx: { display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 } },
      React.createElement(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 2 } },
        images.map(img => React.createElement(ZoomableImage, { key: img.id, src: imgSrc(img.id, img.src), alt: img.alt, label: img.label, onReplace: canUploadImages ? (file: File) => handleImageReplace(img.id, file) : undefined }))
      ),
      React.createElement(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 2 } },
        mergedBlocks.map((block, i) => React.createElement(EditableTextBlock, { key: i, pageId: PAGE_ID, blockId: `block_${i}`, heading: block.heading, body: block.body, onSave: (newBody: string) => handleTextSave(`block_${i}`, newBody) })),
        React.createElement(Box, { sx: { display: 'flex', gap: 1, mt: 2 } }, React.createElement(Button, { variant: 'outlined', onClick: () => navigate('/units/m6_u4_x99_quiz') }, 'Zum Quiz'))
      )
    )
  );
};

export default Page;


